import ky, { HTTPError } from "ky";
// Import direct (pas via @/core/auth/index) : l'index ré-exporte aussi les
// composants React (RequireAuth) — la couche API reste sans UI.
import { keycloakLogin } from "@/core/auth/keycloak";
import { isPasswordGrantMode, stopTokenRefresh } from "@/core/auth/password-grant";
import { getCurrentLanguage } from "@/core/utils/func/language-resolver";
import { useSessionStore } from "@/core/stores/session.store";
import { logApiError, readRequestBody } from "@/core/utils/func/apiErrorLog";
import { getDeviceHeaders } from "@/core/utils/func/deviceHeaders";

/** x-api-key uniquement si l'env le définit — sinon le header est omis. */
const xApiKey = import.meta.env.VITE_xApiKey || "";

/**
 * Header x-api-key sous forme spreadable pour les appels fetch natifs
 * (hors ky). `{}` quand VITE_xApiKey n'est pas défini.
 */
export const getApiKeyHeader = (): Record<string, string> =>
  xApiKey ? { "x-api-key": xApiKey } : {};

/** Langue envoyée dans Accept-Language — résolue par le résolveur canonique de `core`. */
export const getAcceptLanguage = (): string => getCurrentLanguage();

/**
 * Instance publique sans auth (login, refresh, register, endpoints publics).
 *
 * Règle : endpoints publics sans session → `kyPublicInstance` (via
 * `mutatePublicData`) ; endpoints authentifiés → `kyInstance` (via
 * `mutateData`). Jamais de Bearer ici : un token périmé sur login/refresh
 * provoquerait un 401 parasite.
 */
export const kyPublicInstance = ky.create({
  retry: 0,
  // Timeout global : une requête qui hang ne doit pas laisser un loader
  // tourner indéfiniment (règle loaders). Surchargeable par appel.
  timeout: 30_000,
  hooks: {
    beforeRequest: [
      ({ request }) => {
        if (xApiKey) request.headers.set("x-api-key", xApiKey);
        request.headers.set("Accept-Language", getAcceptLanguage());
        Object.entries(getDeviceHeaders()).forEach(([k, v]) =>
          request.headers.set(k, v)
        );
      },
    ],
  },
});

/**
 * Instance authentifiée : Bearer token, log des erreurs, logout sur 401.
 *
 * Règle : endpoints authentifiés → `kyInstance` (via `mutateData`) ;
 * endpoints publics sans session (login, refresh, register) →
 * `kyPublicInstance` (via `mutatePublicData`) — sinon un Bearer périmé est
 * envoyé et le hook beforeError viderait la session sur le 401.
 */
export const kyInstance = kyPublicInstance.extend({
  hooks: {
    beforeRequest: [
      ({ request }) => {
        const token = useSessionStore.getState().accessToken;
        if (token) request.headers.set("Authorization", `Bearer ${token}`);
        if (xApiKey) request.headers.set("x-api-key", xApiKey);
        Object.entries(getDeviceHeaders()).forEach(([k, v]) =>
          request.headers.set(k, v)
        );
      },
    ],
    beforeError: [
      async ({ request, error }) => {
        const requestBody = await readRequestBody(request);

        if (error instanceof HTTPError) {
          // ky ne parse pas le corps d'erreur : on l'attache à `error.data`
          // (clone pour ne pas consommer la réponse) afin que
          // `getBackendErrorMessage`/`apiErrorHandler` lisent le `errorMsg`
          // du format d'erreur Flash Pay (guide §4.8).
          try {
            (error as HTTPError & { data?: unknown }).data = await error.response
              .clone()
              .json();
          } catch {
            /* corps vide ou non-JSON */
          }

          logApiError({
            config: {
              method: request.method,
              url: request.url,
              data: requestBody,
            },
            response: {
              status: error.response.status,
              data: error.data as
                | ({ code?: string | number; message?: unknown } & Record<
                    string,
                    unknown
                  >)
                | undefined,
            },
            message: error.message,
          });
        } else {
          logApiError({
            url: request.url,
            method: request.method,
            message: error.message,
            requestData: requestBody,
          });
        }

        if (error instanceof HTTPError && error.response.status === 401) {
          const hadToken = !!useSessionStore.getState().accessToken;
          if (hadToken) {
            useSessionStore.getState().clearSession();
            // Token rejeté par le gateway : retour à la connexion, selon le
            // mode d'auth actif (formulaire maison | SSO Keycloak).
            if (isPasswordGrantMode()) {
              stopTokenRefresh();
              window.location.assign("/login");
            } else {
              keycloakLogin();
            }
          }
        }

        return error;
      },
    ],
  },
});
